// components/BankAccountRequiredModal.jsx

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { isAuthenticated } from "../auth";
import { getListExternalAccounts } from "../auth/stripe";

import {

  shouldShowBankAccountModal,
  getBankAccountMessage
} from "../pageInside/Rolle";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["bankAccountRequiredModal"]?.[key] || fallback;

// "Later" only dismisses the modal for this browser tab's session -- never
// flips any bank-account-exists state (that stays backend/Stripe-sourced
// only, see hasBankAccount below). sessionStorage (not localStorage) so a
// closed/reopened tab -- a fresh session -- re-evaluates normally.
const LATER_DISMISSED_KEY = "elakayBankAccountModalDismissed";
const wasDismissedThisSession = () => {
  try { return sessionStorage.getItem(LATER_DISMISSED_KEY) === "1"; } catch { return false; }
};
const markDismissedThisSession = () => {
  try { sessionStorage.setItem(LATER_DISMISSED_KEY, "1"); } catch { /* private mode / blocked storage: no-op */ }
};

// Module-level cache of the "has a bank account" check -- avoids re-calling
// getListExternalAccounts on every route change between the pages this
// modal can appear on (spec: don't hit the API on every render/route
// change). Keyed by stripeAccountId since that's what actually identifies
// which Stripe Connect account the list belongs to.
const BANK_CACHE_TTL_MS = 60000;
let __bankAccountsCache = null; // { stripeAccountId, hasBankAccount, fetchedAt }

const getCachedHasBankAccount = (stripeAccountId, token, { forceRefresh = false } = {}) => {
  const cached = __bankAccountsCache;
  const isFresh = cached
    && cached.stripeAccountId === stripeAccountId
    && Date.now() - cached.fetchedAt < BANK_CACHE_TTL_MS;
  if (isFresh && !forceRefresh) return Promise.resolve(cached.hasBankAccount);

  return getListExternalAccounts(token).then((externalAccounts) => {
    // The Stripe call failed (network, Stripe down, or a live Connect account
    // read with test keys in dev: "Only live keys can access this method").
    // That is "unknown", not "no bank account": never cache it and never
    // claim the seller has to add one.
    if (!externalAccounts || externalAccounts.success === false || externalAccounts.error) return null;
    const bankAccounts = Array.isArray(externalAccounts?.data)
      ? externalAccounts.data
      : Array.isArray(externalAccounts)
        ? externalAccounts
        : [];
    const hasBankAccount = bankAccounts.length > 0;
    __bankAccountsCache = { stripeAccountId, hasBankAccount, fetchedAt: Date.now() };
    return hasBankAccount;
  });
};

const CSS_ = `
.bank-modal-overlay,
.bank-modal-overlay *{
  box-sizing:border-box;
}

.bank-modal-overlay{
  position:fixed;
  inset:0;
  width:100vw;
  height:100dvh;
  z-index:999999999;

  background:rgba(13,45,61,.55);
  backdrop-filter:blur(8px);

  display:flex;
  align-items:center;
  justify-content:center;

  padding:16px;

  overflow:hidden;

  overscroll-behavior:none;
  touch-action:none;
}

.bank-modal{
  width:min(560px,100%);
  max-height:calc(100dvh - 32px);

  background:#ffffff;

  border-radius:22px;

  box-shadow:
    0 30px 90px rgba(0,0,0,.25),
    0 0 0 1px rgba(0,115,153,.08);

  overflow-y:auto;

  overscroll-behavior:contain;
  -webkit-overflow-scrolling:touch;

  padding:28px;

  color:#0d2d3d;

  position:relative;
}

.bank-modal::-webkit-scrollbar{
  width:6px;
}

.bank-modal::-webkit-scrollbar-thumb{
  background:#00739955;
  border-radius:20px;
}

.bank-modal-icon{
  width:62px;
  height:62px;

  border-radius:18px;

  background:
    linear-gradient(
      135deg,
      #007399,
      #00a4d8
    );

  display:flex;
  align-items:center;
  justify-content:center;

  font-size:28px;

  margin-bottom:18px;

  box-shadow:
    0 12px 30px rgba(0,115,153,.25);
}

.bank-modal-title{
  margin:0 0 14px;

  font-size:30px;
  font-weight:800;

  color:#004b63;

  line-height:1.1;
}

.bank-modal-message{
  margin:0;

  font-size:16px;
  line-height:1.75;

  color:#334155;
}

.bank-modal-notice{
  margin-top:18px;

  padding:15px;

  border-radius:14px;

  background:#eff8ff;

  border:1px solid #bae6fd;

  color:#075985;

  font-size:14px;
  line-height:1.65;
}

.bank-modal-actions{
  display:flex;
  justify-content:flex-end;
  gap:12px;

  margin-top:26px;
}

.bank-modal-btn{
  border:none;

  border-radius:12px;

  padding:12px 18px;

  font-size:14px;
  font-weight:700;

  cursor:pointer;

  transition:.2s;
}

.bank-modal-btn:hover{
  transform:translateY(-1px);
}

.bank-modal-btn-secondary{
  background:#ffffff;

  border:1px solid #cbd5e1;

  color:#334155;
}

.bank-modal-btn-primary{
  background:
    linear-gradient(
      135deg,
      #007399,
      #00a4d8
    );

  color:#ffffff;

  box-shadow:
    0 10px 24px rgba(0,115,153,.25);
}

@media(max-width:600px){

  .bank-modal{
    padding:22px;
  }

  .bank-modal-title{
    font-size:25px;
  }

  .bank-modal-actions{
    flex-direction:column-reverse;
  }

  .bank-modal-btn{
    width:100%;
  }
}
`;

export default function BankAccountRequiredModal({
  userInfo,
  infoSource
}) {

  __setElakayInfoSource(infoSource);

  const location = useLocation();
  const navigate = useNavigate();
  const { token } = isAuthenticated();
  const pathname = location.pathname;
  const search = location.search;
  const stripeAccountId = String(userInfo?.stripeAccountsId || "").trim();

  const [show, setShow] = React.useState(false);
  // Tri-state guard (spec: never show the modal before the bank-account
  // check has actually resolved) -- starts true so the very first render
  // never flashes the modal open before checkBankRequirement below runs.
  const [checking, setChecking] = React.useState(true);
  // Tracks the previous pathname across renders so we can detect "the user
  // just navigated away from /wallet" and force a fresh (non-cached) check --
  // the one moment a just-added bank account needs to be reflected promptly
  // instead of waiting out the cache TTL.
  const previousPathnameRef = React.useRef(pathname);

  useEffect(() => {
    let active = true;
    const currentLocation = { pathname, search };
    const leftWalletPage = previousPathnameRef.current === "/wallet" && pathname !== "/wallet";
    previousPathnameRef.current = pathname;

    const checkBankRequirement = async () => {
      setChecking(true);

      // Quick, synchronous checks first (page/role/Animation-Settings +
      // this-session "Later" dismissal) -- skips the Stripe call entirely
      // when the modal has no business appearing here regardless of bank
      // status.
      if (!shouldShowBankAccountModal(infoSource, currentLocation, false) || wasDismissedThisSession()) {
        if (active) { setShow(false); setChecking(false); }
        return;
      }

      if (!stripeAccountId || !token) {
        if (active) { setShow(true); setChecking(false); }
        return;
      }

      const hasBankAccount = await getCachedHasBankAccount(stripeAccountId, token, { forceRefresh: leftWalletPage });

      if (active) {
        setShow(hasBankAccount !== null && shouldShowBankAccountModal(infoSource, currentLocation, hasBankAccount));
        setChecking(false);
      }
    };

    checkBankRequirement();
    return () => { active = false; };
  }, [pathname, search, stripeAccountId, token, infoSource]);

  useEffect(() => {

    if (!show) return;

    const scrollY = window.scrollY;

    /* LOCK BODY */
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    document.documentElement.style.overflow = "hidden";

    // The app's real scrolling container is .elakayPage (Layout.css:
    // height:100vh; overflow-y:auto), never document.body/html directly --
    // this is the element the visible, draggable scrollbar actually
    // belongs to. Locking only body/html (above) leaves that scrollbar
    // fully usable, letting the page scroll underneath this modal despite
    // it being "locked". Setting overflow:hidden here is what actually
    // removes/disables that scrollbar -- wheel/touchmove interception
    // (below) never catches a direct scrollbar-thumb drag, only wheel/touch
    // gestures.
    const scrollContainer = document.querySelector(".elakayPage");
    const prevContainerOverflow = scrollContainer ? scrollContainer.style.overflow : null;
    const containerScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
    if (scrollContainer) {
      scrollContainer.style.overflow = "hidden";
    }

    /* PREVENT BACKGROUND SCROLL */
    const preventBackgroundScroll = (e) => {

      const modal = document.querySelector(".bank-modal");

      if (
        modal &&
        modal.contains(e.target)
      ) {
        return;
      }

      e.preventDefault();
    };

    window.addEventListener(
      "wheel",
      preventBackgroundScroll,
      { passive:false }
    );

    window.addEventListener(
      "touchmove",
      preventBackgroundScroll,
      { passive:false }
    );

    return () => {

      window.removeEventListener(
        "wheel",
        preventBackgroundScroll
      );

      window.removeEventListener(
        "touchmove",
        preventBackgroundScroll
      );

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";

      document.documentElement.style.overflow = "";

      if (scrollContainer) {
        scrollContainer.style.overflow = prevContainerOverflow || "";
        scrollContainer.scrollTop = containerScrollTop;
      }

      window.scrollTo(0, scrollY);
    };

  }, [show]);

  // Never render (not even the closed state) before the check above has
  // actually resolved -- avoids a page-load flash of the modal followed by
  // it disappearing once the bank-account/settings check comes back.
  if (checking || !show) return null;

  const content = getBankAccountMessage(infoSource);

  // Portaled straight to document.body -- same as every other modal in the
  // app (react-bootstrap's <Modal> does this internally). This component
  // renders deep inside Footer.js, nested under .elakayPage; without a
  // portal, .bank-modal-overlay's position:fixed still centers on the
  // viewport in most cases, but sits behind/inside the normal DOM stacking
  // order of that ancestor chain instead of at the document root like every
  // other modal -- portaling removes any dependency on ancestor overflow/
  // stacking-context quirks entirely, which is what "like the other
  // modals" means here.
  return typeof document !== "undefined" ? createPortal(
    <>
      <style>{CSS_}</style>

      <div className="bank-modal-overlay">

        <div className="bank-modal">

          <div className="bank-modal-icon">
            💳
          </div>

          <h2 className="bank-modal-title">
            {content.title}
          </h2>

          <p className="bank-modal-message">
            {content.message}
          </p>

          <div className="bank-modal-notice">{__elakayText("t001", "All eligible payments, commissions, and revenue will be transferred securely to your connected bank account after delivery validation and pending transfer verification.")}</div>

          <div className="bank-modal-actions">

            <button
              type="button"
              className="
                bank-modal-btn
                bank-modal-btn-secondary
              "
              onClick={() => { markDismissedThisSession(); setShow(false); }}
            >{__elakayText("t003", "Later")}</button>

            <button
              type="button"
              className="
                bank-modal-btn
                bank-modal-btn-primary
              "
              onClick={() =>
                navigate("/wallet?bkl=true")
              }
            >{__elakayText("t002", "Add bank account")}</button>

          </div>

        </div>

      </div>
    </>,
    document.body
  ) : null;
}
